/**
 * Ключ и код темы организации (#845, спека docs/plans/2026-10-02-conversation-themes.md
 * §2 и §4 «Код темы»). ОДИН модуль на все пути, которые заводят тему: ручной
 * ввод (PR 2), стартовое предложение (PR 4), принятие предложения недельной
 * сводки (PR 5). Две функции здесь решают разное:
 *
 * - `normalizeThemeKey` — ключ дедупликации (`org_themes.norm_key`,
 *   `UNIQUE(org_id, norm_key)`) на ЯЗЫКЕ организации: «Доставка в регионы»,
 *   «доставка  в регионы» и «"Доставка в регионы"» — одна тема. Та же
 *   нормализация сводит фразы `topic`/`suggested_theme` в предложениях (§2 Р1,
 *   §4) — иначе ручная тема и предложенная разошлись бы по ключу.
 * - `generateThemeCode` — ASCII-идентификатор, который видит модель и хранят
 *   факты (`^[a-z][a-z0-9_]{0,39}$`, `uncategorized` зарезервирован). Строится
 *   из названия, а не из ключа, детерминированно: одно название — одна основа,
 *   коллизия в наборе (архив включительно) — суффикс `_2`, `_3`…
 *
 * Без импорта значений из `@aica/api-types`: кольцо core ↔ api-types ломается
 * от любого value-импорта (#1076). Контракт Zod берёт константы отсюда.
 */
import { normalizeLabelKey } from './normalize';

/** Форма кода темы — та же, что CHECK `org_themes.code` (миграция 202610082305). */
export const THEME_CODE_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;

export const THEME_CODE_MAX_LENGTH = 40;

/** Ответ модели «ни одна тема не подходит» (§3): темой быть не может. */
export const RESERVED_THEME_CODE = 'uncategorized';

/** Код названия, от которого после транслитерации ничего не осталось. */
export const FALLBACK_THEME_CODE = 'theme';

/**
 * Начало кода темы демо-набора (#2008). `generateThemeCode` сжимает повторы `_`,
 * поэтому код с `__` у темы клиента не появится никогда: демо-набор и набор
 * клиента не делят ни одного кода, хотя `UNIQUE(org_id, code)` у них общий.
 */
export const DEMO_THEME_CODE_PREFIX = 'demo__';

/** Префикс кода, чья основа начинается не с буквы (`2024_sale` → `t_2024_sale`). */
const NON_LETTER_PREFIX = 't_';

// --- Ключ ---------------------------------------------------------------------

// Апострофы внутри слова («зв'язок», «obj'ekt») удаляются, а не превращаются в
// пробел: «звязок», набранный без апострофа, — то же слово. `normalizeLabelKey`
// уже свёл все их начертания к одному «'».
const APOSTROPHE = /'/g;

// Всё, что не буква и не цифра, — разделитель слов: «доставка/сроки»,
// «доставка, сроки» и «доставка — сроки» дают один ключ.
const NON_WORD = /[^\p{L}\p{N}]+/gu;

/**
 * Невидимое внутри слова не разрывает его: знаки формата (`\p{Cf}` — ZWSP,
 * ZWJ, мягкий перенос, bidi-управление) и свободные комбинирующие знаки
 * (`\p{M}`), оставшиеся после NFKC, — ударение «доста́вка» (у «а» с акутом
 * нет составной формы). Без этого `NON_WORD` резал бы «доста́вка» и
 * «Лиз\u200Bинг» на два слова: ключ расходился с обычным написанием, и дубль
 * и совпадение с категорией проходили мимо. Составные буквы («й», «ї», «é»)
 * после NFKC — одна кодовая точка, а не знак: их это не трогает.
 */
const INVISIBLE = /[\p{Cf}\p{M}]/gu;

/**
 * Снимает диакритику только с ЛАТИНСКИХ букв: «facturación» = «facturacion»,
 * «año» = «ano». Кириллица не трогается: в NFD «й» — это «и» с бреве, и снятие
 * знака слило бы «мой» с «мои»; «ї» — «і» с умлаутом, то же самое.
 */
function stripLatinDiacritics(value: string): string {
  return value
    .normalize('NFD')
    .replace(/(\p{Script=Latin})\p{M}+/gu, '$1')
    .normalize('NFC');
}

/**
 * Ключ дедупликации темы. Пустая строка — «ключа нет» (название из одних
 * знаков препинания): такое название темой стать не может.
 *
 * Неподвижная точка: `normalizeThemeKey(normalizeThemeKey(x)) === normalizeThemeKey(x)`.
 */
export function normalizeThemeKey(raw: unknown): string {
  const label = normalizeLabelKey(raw);
  if (!label) return '';
  return stripLatinDiacritics(label.replace(INVISIBLE, '').replace(/ё/g, 'е'))
    .replace(APOSTROPHE, '')
    .replace(NON_WORD, ' ')
    .trim();
}

// --- Транслитерация -----------------------------------------------------------

/**
 * Русский — ГОСТ 7.79-2000, система Б, без знаков, которых в коде всё равно
 * не будет: ъ и ь (в системе Б — `` и `) опускаются, ы — `y` (в системе Б
 * `y'`), э — `e` (`e'`). Ц — `cz` перед e, i, y, j и `c` в остальных случаях
 * (правило системы Б, см. `transliterateRussian`).
 */
const RU: Readonly<Record<string, string>> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'yo',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'j',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'x',
  ч: 'ch',
  ш: 'sh',
  щ: 'shh',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

/**
 * Украинский — паспортная транслитерация КМУ 2010 (постановление № 55):
 * є, ї, й, ю, я в начале слова — ye, yi, y, yu, ya, в остальных позициях —
 * ie, i, i, iu, ia; «зг» — zgh; мягкий знак и апостроф опускаются.
 */
const UK: Readonly<Record<string, string>> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'h',
  ґ: 'g',
  д: 'd',
  е: 'e',
  є: 'ie',
  ж: 'zh',
  з: 'z',
  и: 'y',
  і: 'i',
  ї: 'i',
  й: 'i',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ь: '',
  ю: 'iu',
  я: 'ia',
};

const UK_WORD_INITIAL: Readonly<Record<string, string>> = {
  є: 'ye',
  ї: 'yi',
  й: 'y',
  ю: 'yu',
  я: 'ya',
};

/** Буквы, которые есть только в украинском алфавите: по ним выбирается КМУ 2010. */
const UKRAINIAN_ONLY = /[ієїґ]/u;

/** Латинские буквы без разложения в NFD. */
const LATIN_EXTRA: Readonly<Record<string, string>> = {
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
  ø: 'o',
  đ: 'd',
  ł: 'l',
  þ: 'th',
};

const isCyrillicLetter = (ch: string | undefined): boolean =>
  ch !== undefined && /\p{Script=Cyrillic}/u.test(ch);

function transliterateRussian(text: string): string {
  let out = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === 'ц') {
      // Система Б: cz перед e, i, y, j (в транслите следующей буквы), иначе c.
      const next = text[i + 1];
      const nextLatin = next !== undefined ? (RU[next] ?? next) : '';
      out += /^[eiyj]/.test(nextLatin) ? 'cz' : 'c';
      continue;
    }
    out += RU[ch] ?? ch;
  }
  return out;
}

function transliterateUkrainian(text: string): string {
  let out = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    const wordInitial = !isCyrillicLetter(text[i - 1]);
    if (ch === 'г' && text[i - 1] === 'з') {
      out += 'gh';
      continue;
    }
    // Русская буква в украинском названии (ы, э, ё, ъ) — по русской таблице,
    // а не выпадает целиком.
    out += (wordInitial ? UK_WORD_INITIAL[ch] : undefined) ?? UK[ch] ?? RU[ch] ?? ch;
  }
  return out;
}

/**
 * Название → латиница нижнего регистра. Язык кириллицы определяется по самому
 * названию, а не по настройке организации: код обязан зависеть только от
 * названия (принятие одного предложения дважды даёт тот же код). Есть хоть одна
 * из «і є ї ґ» — украинский (КМУ 2010), иначе русский (ГОСТ 7.79-2000 Б).
 * Латиница теряет диакритику (испанский: «facturación» → «facturacion»).
 * Что осталось не-ASCII (другие письменности, эмодзи), выпадает на следующем шаге.
 */
export function transliterateThemeName(name: string): string {
  // Сначала невидимое (как в ключе): ZWSP не должен дать «_» посреди слова,
  // а ударение — сбить правило «ц» системы Б, глядящее на следующую букву.
  const lower = name
    .normalize('NFC')
    .replace(INVISIBLE, '')
    .toLowerCase()
    .replace(/[’ʼ']/g, '');
  const cyrillic = UKRAINIAN_ONLY.test(lower)
    ? transliterateUkrainian(lower)
    : transliterateRussian(lower);
  const latinExtra = cyrillic.replace(/[ßæœøđłþ]/g, (ch) => LATIN_EXTRA[ch] ?? ch);
  return latinExtra.normalize('NFD').replace(/\p{M}+/gu, '');
}

// --- Код ----------------------------------------------------------------------

/**
 * Основа кода без суффикса коллизии: транслитерация, всё кроме `[a-z0-9]` → `_`,
 * повторы сжаты, края без `_`; не с буквы — префикс `t_`; ПОСЛЕ префикса обрезка
 * до 40 знаков; пусто — `theme`. Результат всегда проходит `THEME_CODE_PATTERN`
 * (а `uncategorized` отсекает `themeCodeCandidate`).
 */
export function themeCodeBase(name: string): string {
  const ascii = transliterateThemeName(name)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!ascii) return FALLBACK_THEME_CODE;
  const prefixed = /^[a-z]/.test(ascii) ? ascii : `${NON_LETTER_PREFIX}${ascii}`;
  return trimTrailingUnderscores(prefixed.slice(0, THEME_CODE_MAX_LENGTH));
}

function trimTrailingUnderscores(value: string): string {
  return value.replace(/_+$/, '');
}

/**
 * Кандидат номер `attempt` (1, 2, 3…): первый — сама основа, дальше — основа,
 * обрезанная так, чтобы с суффиксом `_<n>` уложиться в 40 знаков. `null` —
 * кандидат зарезервирован (`uncategorized`) и пропускается.
 */
export function themeCodeCandidate(base: string, attempt: number): string | null {
  if (!Number.isInteger(attempt) || attempt < 1) {
    throw new RangeError(`themeCodeCandidate: attempt must be a positive integer`);
  }
  const candidate =
    attempt === 1
      ? base
      : (() => {
          const suffix = `_${attempt}`;
          const stem = trimTrailingUnderscores(
            base.slice(0, THEME_CODE_MAX_LENGTH - suffix.length)
          );
          return `${stem}${suffix}`;
        })();
  return candidate === RESERVED_THEME_CODE ? null : candidate;
}

/**
 * Первый свободный код для названия: ни один из `taken` (коды набора
 * организации, архив включительно) и не `uncategorized`. Конечен: среди
 * `taken.size + 2` кандидатов хотя бы один свободен.
 */
export function generateThemeCode(name: string, taken: ReadonlySet<string>): string {
  const base = themeCodeBase(name);
  for (let attempt = 1; ; attempt++) {
    const candidate = themeCodeCandidate(base, attempt);
    if (candidate !== null && !taken.has(candidate)) return candidate;
  }
}
